import React, { useCallback, useMemo, useState } from 'react';
import cookie from 'react-cookies';
import { useDispatch, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { setPhone } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers';
import { formatPhone } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers/set-phone';
import { TSpecOfferSetPhoneResponse } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers/set-phone/dto/responseDto';

import { IDefaultPopup } from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { CreatablePhoneSelect, IOption } from '@r1-frontend/ui-react/components/select/creatableSelect';

import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import { LkPromotionalPopupExtra } from '~/src/features/specialOffersContainer/containers/popups/LkPromotionalPopupExtra';
import { setPopupState } from '~/src/features/specialOffersContainer/store/actions';
import { TSpecOffer } from '~/src/features/specialOffersContainer/types';
import { TPromoCallbackMap } from '~/src/features/specialOffersContainer/types';
import { TState } from '~/src/store';

import * as ST from './styled';

export const PromoNeedSetPhonePopup = ({
    offer,
    onActivate,
    ...props
}: IDefaultPopup & Pick<TPromoCallbackMap<typeof DefaultCallback>, 'onActivate'> & {
    offer: TSpecOffer,
    }) => {
    const { clientContacts } = useSelector((state: TState) => state.auth);

    const [isLocalLoading, setIsLocalLoading] = useState(false);
    const [phoneOptions, setPhoneOptions] = useState<IOption[]>(
        (clientContacts?.phone?.filter(({ status }) => status === 'confirmed') || []).map((item) => ({
            label: item.value,
            value: `${item.id}`,
        })),
    );
    const [inputValue, setInputValue] = useState<IOption>();
    const [selectedPhone, setSelectedPhone] = useState<IOption>(
        phoneOptions.slice(0, 1)[0] || {
            value: 0,
            label: '',
        },
    );
    const [isNumberUsed, setIsNumberUsed] = useState(false);
    const [useMargin, setUseMargin] = useState(true);

    const subscriptions = useMemo(
        () =>
            (offer.benefits || [])
                .filter(({ needPhone }) => needPhone)
                .map(({ title }) => title)
                .join(', '),
        [offer],
    );
    const dispatch = useDispatch();

    const onInputChange = useCallback((option: IOption) => {
        setInputValue(option);

        const { value } = option;
        const phoneValue = value || '';

        if (phoneValue && phoneValue.length < 11) {
            return;
        }

        const newOption = {
            value,
            label: value,
        };

        setPhoneOptions([
            ...phoneOptions,
            { ...newOption },
        ]);
        setSelectedPhone(newOption);
    }, [phoneOptions]);

    const onSetPhone = useCallback(async() => {
        const { value, label } = selectedPhone;
        const providerId = cookie.load('providerId');

        const phoneParam = value === label
            ? { phone: formatPhone(value) }
            : { contactId: value };

        setIsLocalLoading(true);

        const response = await doRequest.specOffer<TSpecOfferSetPhoneResponse>(
            setPhone(providerId, {
                requestId: offer.requestId,
                ...phoneParam,
            }),
        );

        setIsLocalLoading(false);

        if (!response.isSuccess) {
            specialOfferAnalytics.errorPhoneCheck(offer);
            return dispatch(setPopupState('connectionError'));
        }

        const { payload } = response;

        setIsNumberUsed(!payload);

        if (payload) {
            const { callback } = onActivate(offer);

            callback();
            specialOfferAnalytics.successPhoneCheck(offer);
        }
    }, [offer, onActivate, selectedPhone]);

    const onPhoneChange = useCallback((option: IOption) => {
        setSelectedPhone(option);
        setInputValue(undefined);
        setIsNumberUsed(false);
    }, []);

    return (
        <LkPromotionalPopupExtra
            message={`Укажите номер для подключения подписки ${subscriptions}`}
            icon={false}
            buttons={() => [
                <ST.PopupSetPhoneWrapper
                    isSelectInputable={selectedPhone.value < 0}
                    useMargin={useMargin}
                    key={'btnSelectPhone'}
                    className={'ym-hide-content'}
                >
                    <CreatablePhoneSelect
                        value={selectedPhone}
                        placeholder={'Выберите или введите номер'}
                        createLabel={'Указать новый номер'}
                        options={phoneOptions}
                        onChange={onPhoneChange}
                        inputValue={inputValue?.value}
                        onInputChange={onInputChange}
                        inputPlaceHolder={'Номер телефона'}
                        onMenuOpen={() => setUseMargin(false)}
                        onMenuClose={() => setUseMargin(true)}
                        masked
                    />
                </ST.PopupSetPhoneWrapper>,
                <ST.PopupSetPhoneError key={'phoneErrorMessage'}>
                    {isNumberUsed
                        ? `На данный номер телефона уже подключена подписка ${subscriptions}. Введите другой номер.`
                        : null}
                </ST.PopupSetPhoneError>,
                <ST.PopupSetPhoneButton
                    useMargin={useMargin}
                    key={'btnContinue'}
                    data-test={'LkPromotionalPopupExtra-btntoactions'}
                    loading={offer.isConnecting || isLocalLoading}
                    disabled={selectedPhone.value <= 0 || isNumberUsed || offer.isLoading}
                    onClick={onSetPhone}
                >
                    Продолжить
                </ST.PopupSetPhoneButton>,
            ]}
            {...props}
        />
    );
};
